import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Card,
    Chip,
    Divider,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockIcon from '@mui/icons-material/Lock';
import ClientDashboardLayout from '../ClientDashboardLayout';
import { useNavigate, useParams } from 'react-router-dom';
import { getMetricData } from '../../../api/metric';
import { getCategoryBreakdown } from '../../../api/expenseBreakdown';
import { getCustomerScenario } from '../../../api/scenarios';

const moneyFmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

const PANEL_MIN_HEIGHT_LG = 720;
const PANEL_HEIGHT_LG = 'calc(100vh - 260px)';

const RESPONSIVE = {
    pageTitleFontSize: { xs: 24, xl: 28 },
    pageTitleLineHeight: { xs: 1.334, xl: 1.25 },
    headerPaddingX: { xs: 2, sm: 3, xl: 4 },
    panelInnerPaddingX: { xs: '16px', xl: '20px' },
    panelHeaderPaddingY: { xs: '20px', xl: '24px' },
    metricValueFontSize: { xs: 15, xl: 16 },
};

const formatMoney = (n) => {
    const v = Number(n || 0);
    if (!Number.isFinite(v)) return '$0';
    return moneyFmt.format(v);
};

const formatPercent1 = (n) => {
    const v = Number(n || 0);
    if (!Number.isFinite(v)) return '0.0%';
    return `${v.toFixed(1)}%`;
};

const toDateStr = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const sections = [
    { title: 'Income', group: 'income' },
    { title: 'Cost of Goods Sold', group: 'cogs' },
    { title: 'Advertising & Marketing', group: 'advertising_marketing' },
    { title: 'Team', group: 'team' },
    { title: 'Practice Costs', group: 'practice_costs' },
    { title: 'Overheads', group: 'overheads' },
    { title: 'Business Development', group: 'business_development' },
    { title: 'Taxes', group: 'taxes' },
    { title: 'Other Income', group: 'other_income' },
    { title: 'Other Expenses', group: 'other_expense' },
];

const Metric = ({ label, value, valueColor }) => {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
            <Typography
                sx={{
                    fontFamily: '"Rethink Sans"',
                    fontWeight: 400,
                    fontSize: 12,
                    lineHeight: 1.43,
                    letterSpacing: '0.17px',
                    color: 'rgba(0,0,0,0.6)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}
            >
                {label}
            </Typography>
            <Typography
                sx={{
                    fontFamily: '"Rethink Sans"',
                    fontWeight: 600,
                    fontSize: RESPONSIVE.metricValueFontSize,
                    lineHeight: 1.57,
                    letterSpacing: '0.1px',
                    color: valueColor || 'rgba(0,0,0,0.87)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}
            >
                {value}
            </Typography>
        </Box>
    );
};

const TotalRow = ({ title, total }) => {
    return (
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: RESPONSIVE.panelInnerPaddingX, py: '10px', border: '1px solid rgba(0,0,0,0.06)', borderRadius: '10px', bgcolor: '#FDFDFD' }}
        >
            <Typography sx={{ fontFamily: '"Rethink Sans"', fontWeight: 600, fontSize: 15, lineHeight: 1.57, letterSpacing: '0.1px', color: 'rgba(0,0,0,0.87)' }}>
                {title}
            </Typography>
            <Typography sx={{ fontFamily: '"Rethink Sans"', fontWeight: 600, fontSize: 14, lineHeight: 1.57, letterSpacing: '0.1px', color: 'rgba(0,0,0,0.87)' }}>
                {total}
            </Typography>
        </Stack>
    );
};

export default function ScenarioDetail() {
    const navigate = useNavigate();
    const { scenarioId } = useParams();
    const [customerId, setCustomerId] = useState(() => sessionStorage.getItem('selectedCustomerId'));
    const todayStr = useMemo(() => toDateStr(new Date()), []);

    const [scenario, setScenario] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [baselineMode, setBaselineMode] = useState('historical');

    const [currentBaseline, setCurrentBaseline] = useState({
        netProfit: 0,
        profitMarginPct: 0,
        revenue: 0,
        expenses: 0,
    });
    const [currentBreakdowns, setCurrentBreakdowns] = useState({});
    const [currentLoading, setCurrentLoading] = useState(false);

    useEffect(() => {
        const syncFromStorage = () => {
            setCustomerId(sessionStorage.getItem('selectedCustomerId'));
        };
        window.addEventListener('selectedCustomerId-changed', syncFromStorage);
        return () => window.removeEventListener('selectedCustomerId-changed', syncFromStorage);
    }, []);

    useEffect(() => {
        if (!customerId || !scenarioId) return;
        const controller = new AbortController();

        setLoadError(null);
        getCustomerScenario(customerId, scenarioId, {}, controller.signal)
            .then((res) => {
                setScenario(res?.data || null);
            })
            .catch(() => {
                setLoadError('Failed to load scenario.');
                setScenario(null);
            });

        return () => controller.abort();
    }, [customerId, scenarioId]);

    useEffect(() => {
        if (baselineMode !== 'current') return;
        if (!customerId) return;

        const controller = new AbortController();
        setCurrentLoading(true);

        const load = async () => {
            try {
                const metricRes = await getMetricData(customerId, todayStr, controller.signal, { adjusted: false, ytd: false });
                const data = metricRes?.data || {};
                setCurrentBaseline({
                    netProfit: Number(data.profit) || 0,
                    profitMarginPct: Number(data.profit_percentage) || 0,
                    revenue: Number(data.income) || 0,
                    expenses: Number(data.expense) || 0,
                });

                const groupResults = await Promise.all(
                    sections.map((s) =>
                        getCategoryBreakdown(customerId, todayStr, s.group, controller.signal).then((res) => ({
                            group: s.group,
                            payload: res?.data,
                        }))
                    )
                );

                const next = {};
                groupResults.forEach((r) => {
                    if (r?.group && r?.payload) {
                        next[r.group] = r.payload;
                    }
                });
                setCurrentBreakdowns(next);
            } catch (err) {
                if (err?.name !== 'CanceledError' && err?.name !== 'AbortError') {
                    setCurrentBaseline({ netProfit: 0, profitMarginPct: 0, revenue: 0, expenses: 0 });
                    setCurrentBreakdowns({});
                }
            } finally {
                if (!controller.signal.aborted) {
                    setCurrentLoading(false);
                }
            }
        };

        load();
        return () => controller.abort();
    }, [baselineMode, customerId, todayStr]);

    const historicalBaseline = scenario?.baseline_metrics || {};
    const historicalBreakdowns = scenario?.baseline_breakdowns || {};
    const scenarioMetrics = scenario?.scenario_metrics || {};
    const lineItems = Array.isArray(scenario?.line_items) ? scenario.line_items : [];

    const getHistoricalTotal = (group) => {
        const payload = historicalBreakdowns?.[group];
        const total = payload?.total;
        return Number(total) || 0;
    };

    const getCurrentTotal = (group) => {
        const payload = currentBreakdowns?.[group];
        const total = payload?.total;
        return Number(total) || 0;
    };

    const getScenarioTotal = (group) => {
        const total = lineItems.reduce((sum, it) => {
            if (it?.group_key !== group) return sum;
            return sum + (Number(it?.scenario_amount) || 0);
        }, 0);
        return total;
    };

    const baselineHeaderSubtitle =
        baselineMode === 'historical'
            ? `Historical baseline (saved on ${scenario?.baseline_end_date || ''})`
            : 'Current baseline (live)';

    const baselineHeadline =
        baselineMode === 'historical'
            ? {
                  netProfit: Number(historicalBaseline.netProfit) || 0,
                  profitMarginPct: Number(historicalBaseline.profitMarginPct) || 0,
                  revenue: Number(historicalBaseline.revenue) || 0,
                  expenses: Number(historicalBaseline.expenses) || 0,
              }
            : currentBaseline;

    return (
        <ClientDashboardLayout title={null} contentMaxWidth="100%" contentAlign="left">
            <Box sx={{ px: RESPONSIVE.headerPaddingX, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Button
                    variant="text"
                    startIcon={<ArrowBackIcon sx={{ width: 24, height: 24 }} />}
                    onClick={() => navigate('/scenario-planner')}
                    sx={{
                        alignSelf: 'flex-start',
                        textTransform: 'capitalize',
                        px: '0px',
                        py: '6px',
                        borderRadius: '4px',
                        fontFamily: '"Rethink Sans"',
                        fontWeight: 500,
                        fontSize: 14,
                        lineHeight: '24px',
                        letterSpacing: '0.4px',
                        color: '#536DEC',
                        '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                        '& .MuiButton-startIcon': { ml: 0, mr: '8px' },
                    }}
                >
                    Back to Scenarios
                </Button>

                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ gap: 2.5, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
                        <Typography
                            sx={{
                                fontFamily: '"Rethink Sans"',
                                fontSize: RESPONSIVE.pageTitleFontSize,
                                fontWeight: 600,
                                lineHeight: RESPONSIVE.pageTitleLineHeight,
                                color: 'rgba(0,0,0,0.87)',
                            }}
                        >
                            {scenario?.name || 'Scenario'}
                        </Typography>
                        {scenario?.description ? (
                            <Typography sx={{ fontFamily: '"Rethink Sans"', fontSize: 14, color: 'rgba(0,0,0,0.6)' }}>
                                {scenario.description}
                            </Typography>
                        ) : null}
                    </Box>
                </Stack>

                {loadError && (
                    <Typography sx={{ fontFamily: '"Rethink Sans"', fontSize: 13, color: '#D32F2F' }}>
                        {loadError}
                    </Typography>
                )}
            </Box>

            <Divider sx={{ mt: 2 }} />

            <Box
                sx={{
                    px: RESPONSIVE.headerPaddingX,
                    mt: 2,
                    display: 'flex',
                    gap: '20px',
                    width: '100%',
                    flexWrap: { xs: 'wrap', lg: 'nowrap' },
                    alignItems: 'stretch',
                }}
            >
                <Card
                    sx={{
                        width: { xs: '100%', lg: 520 },
                        minWidth: { xs: '100%', lg: 520 },
                        flex: { xs: '1 1 100%', lg: '0 0 auto' },
                        minHeight: { lg: PANEL_MIN_HEIGHT_LG },
                        height: { lg: PANEL_HEIGHT_LG },
                        maxHeight: { lg: PANEL_HEIGHT_LG },
                        borderRadius: '12px',
                        border: '1px solid rgba(0,0,0,0.10)',
                        boxShadow: 'none',
                        overflowY: { lg: 'auto' },
                        overflowX: 'hidden',
                    }}
                >
                    <Box sx={{ position: 'sticky', top: 0, bgcolor: 'white', zIndex: 2 }}>
                        <Box sx={{ px: RESPONSIVE.panelInnerPaddingX, py: RESPONSIVE.panelHeaderPaddingY, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ gap: '12px' }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
                                    <Typography sx={{ fontFamily: '"Rethink Sans"', fontWeight: 600, fontSize: 16, lineHeight: 1.75, letterSpacing: '0.15px', color: 'rgba(0,0,0,0.87)' }}>
                                        Baseline Performance
                                    </Typography>
                                    <Typography sx={{ fontFamily: '"Rethink Sans"', fontWeight: 400, fontSize: 14, lineHeight: 1.43, letterSpacing: '0.17px', color: 'rgba(0,0,0,0.6)' }}>
                                        {baselineHeaderSubtitle}
                                    </Typography>
                                </Box>

                                <Chip
                                    icon={<LockIcon sx={{ width: 20, height: 20, color: 'rgba(0,0,0,0.56)' }} />}
                                    label="Baseline"
                                    size="small"
                                    sx={{
                                        bgcolor: 'rgba(0,0,0,0.08)',
                                        color: 'rgba(0,0,0,0.56)',
                                        fontFamily: '"Rethink Sans"',
                                        fontWeight: 500,
                                        fontSize: 13,
                                        lineHeight: '18px',
                                        letterSpacing: '0.16px',
                                        borderRadius: '999px',
                                        px: '8px',
                                        py: '3px',
                                        '& .MuiChip-icon': { ml: 0, mr: '6px' },
                                    }}
                                />
                            </Stack>

                            <Box sx={{ mt: 2 }}>
                                <ToggleButtonGroup
                                    exclusive
                                    value={baselineMode}
                                    onChange={(_, next) => {
                                        if (!next) return;
                                        setBaselineMode(next);
                                    }}
                                    size="small"
                                >
                                    <ToggleButton value="historical">Historical</ToggleButton>
                                    <ToggleButton value="current">Current</ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        </Box>

                        <Box sx={{ px: RESPONSIVE.panelInnerPaddingX, pt: '16px' }}>
                            <Box sx={{ bgcolor: '#F8F9FC', border: '1px solid rgba(0,0,0,0.06)', borderRadius: '10px', p: '16px' }}>
                                <Typography sx={{ fontFamily: '"Rethink Sans"', fontWeight: 500, fontSize: 14, lineHeight: 1.43, letterSpacing: '0.17px', textTransform: 'uppercase', color: 'rgba(0,0,0,0.87)', mb: '12px' }}>
                                    Baseline Metrics
                                </Typography>

                                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ gap: '12px' }}>
                                    <Metric
                                        label="Net Profit"
                                        value={formatMoney(baselineHeadline.netProfit)}
                                        valueColor={baselineHeadline.netProfit < 0 ? '#D32F2F' : '#2E7D32'}
                                    />
                                    <Divider orientation="vertical" flexItem />
                                    <Metric label="Profit Margin" value={formatPercent1(baselineHeadline.profitMarginPct)} />
                                    <Divider orientation="vertical" flexItem />
                                    <Metric label="Revenue" value={formatMoney(baselineHeadline.revenue)} />
                                    <Divider orientation="vertical" flexItem />
                                    <Metric label="Expenses" value={formatMoney(baselineHeadline.expenses)} />
                                </Stack>
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{ px: RESPONSIVE.panelInnerPaddingX, py: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {currentLoading && baselineMode === 'current' ? (
                            <Typography sx={{ fontFamily: '"Rethink Sans"', fontSize: 14, color: 'rgba(0,0,0,0.6)' }}>
                                Loading current baseline...
                            </Typography>
                        ) : null}
                        {sections.map((s) => {
                            const total =
                                baselineMode === 'historical'
                                    ? getHistoricalTotal(s.group)
                                    : getCurrentTotal(s.group);
                            return <TotalRow key={s.group} title={s.title} total={formatMoney(total)} />;
                        })}
                    </Box>
                </Card>

                <Card
                    sx={{
                        width: { xs: '100%', lg: 520 },
                        minWidth: { xs: '100%', lg: 520 },
                        flex: { xs: '1 1 100%', lg: '0 0 auto' },
                        minHeight: { lg: PANEL_MIN_HEIGHT_LG },
                        height: { lg: PANEL_HEIGHT_LG },
                        maxHeight: { lg: PANEL_HEIGHT_LG },
                        borderRadius: '12px',
                        border: '1px solid #536DEC',
                        boxShadow: 'none',
                        bgcolor: '#FFFFFF',
                        overflowY: { lg: 'auto' },
                        overflowX: 'hidden',
                    }}
                >
                    <Box sx={{ position: 'sticky', top: 0, bgcolor: 'white', zIndex: 2 }}>
                        <Box sx={{ px: RESPONSIVE.panelInnerPaddingX, py: RESPONSIVE.panelHeaderPaddingY, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                            <Typography sx={{ fontFamily: '"Rethink Sans"', fontWeight: 600, fontSize: 16, lineHeight: 1.75, letterSpacing: '0.15px', color: 'rgba(0,0,0,0.87)' }}>
                                Saved Scenario
                            </Typography>
                            <Typography sx={{ fontFamily: '"Rethink Sans"', fontWeight: 400, fontSize: 14, lineHeight: 1.43, letterSpacing: '0.17px', color: 'rgba(0,0,0,0.6)' }}>
                                Always shows what was saved
                            </Typography>
                        </Box>

                        <Box sx={{ px: RESPONSIVE.panelInnerPaddingX, pt: '16px' }}>
                            <Box sx={{ bgcolor: '#F6F7FF', border: '1px solid #CAD2F9', borderRadius: '10px', p: '16px' }}>
                                <Typography sx={{ fontFamily: '"Rethink Sans"', fontWeight: 500, fontSize: 14, lineHeight: 1.43, letterSpacing: '0.17px', textTransform: 'uppercase', color: '#536DEC', mb: '12px' }}>
                                    Scenario Metrics
                                </Typography>

                                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ gap: '12px' }}>
                                    <Metric
                                        label="Net Profit"
                                        value={formatMoney(Number(scenarioMetrics.netProfit) || 0)}
                                        valueColor={(Number(scenarioMetrics.netProfit) || 0) < 0 ? '#D32F2F' : '#2E7D32'}
                                    />
                                    <Divider orientation="vertical" flexItem />
                                    <Metric label="Profit Margin" value={formatPercent1(Number(scenarioMetrics.profitMarginPct) || 0)} />
                                    <Divider orientation="vertical" flexItem />
                                    <Metric label="Revenue" value={formatMoney(Number(scenarioMetrics.revenue) || 0)} />
                                    <Divider orientation="vertical" flexItem />
                                    <Metric label="Expenses" value={formatMoney(Number(scenarioMetrics.expenses) || 0)} />
                                </Stack>
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{ px: RESPONSIVE.panelInnerPaddingX, py: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {sections.map((s) => {
                            const total = getScenarioTotal(s.group);
                            return <TotalRow key={s.group} title={s.title} total={formatMoney(total)} />;
                        })}
                    </Box>
                </Card>
            </Box>
        </ClientDashboardLayout>
    );
}